import {
    Alert,
    AlertTitle,
    Box,
    Button,
    FormHelperText,
    TextField
} from '@mui/material'
import { useFormik } from 'formik'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import * as Yup from 'yup'
import SavingBar from '../../components/saving-bar'
import { useAuth } from '../../hooks/use-auth'
import { useMounted } from '../../hooks/use-mounted'
import { gtm } from '../../lib/gtm'
import AcceptanceTermsCheckbox from './acceptance-terms-checkbox'

export const JWTLogin = (props) => {
    const isMounted = useMounted()
    const router = useRouter()

    const authContext = useAuth()
    const { login } = useAuth()

    const formik = useFormik({
        initialValues: {
            email: '',
            password: '',
            termsAccepted: false,
            termsVersion: null,
            // email: "demo@devias.io",
            // password: "5e86809283e28b96d2d38537",
            submit: null,
            accountAccessErrors: []
        },
        initialErrors: {
            formError: null,
            can_login: null,
            accountAccessErrors: []
        },
        validationSchema: Yup.object({
            email: Yup.string()
                .email('Must be a valid email')
                .max(255)
                .required('Email is required'),
            password: Yup.string().max(255).required('Password is required'),
            termsAccepted: Yup.boolean().oneOf(
                [true],
                'You must accept the Terms & Services'
            )
        }),
        onSubmit: async (values, helpers) => {
            try {
                const { errorMessage, user, can_login, accountAccessErrors } =
                    await login(
                        values.email,
                        values.password,
                        values.termsAccepted,
                        values.termsVersion
                    )

                if (can_login && isMounted() && user) {
                    gtm.push({
                        event: 'userinfo',
                        event_action: 'update',
                        first_name: user.usr_first_name,
                        last_name: user.usr_last_name,
                        email: user.usr_email,
                        phone_number: user.phone_number,
                        company_name: user.usr_company_name,
                        company_url: user.usr_company_url
                    })

                    const returnUrl = router.query.returnUrl || '/dashboard'
                    router.push(returnUrl)
                } else {
                    helpers.setStatus({ success: false })
                    helpers.setErrors({
                        formError: errorMessage,
                        can_login,
                        accountAccessErrors
                    })
                    helpers.setSubmitting(false)
                }
            } catch (err) {
                console.error(err)

                if (isMounted()) {
                    helpers.setStatus({ success: false })
                    helpers.setErrors({ formError: err.message })
                    helpers.setSubmitting(false)
                }
            }
        }
    })

    useEffect(() => {
        if (authContext.isAuthenticated && authContext.user) {
            router.push('/dashboard')
        }
    }, [])

    const isVerified = router.query.hasOwnProperty('account_verified')
    const hasPasswordReset = router.query.hasOwnProperty('password_reset')
    const isApproved = router.query.hasOwnProperty('account_approved')
        ? router.query.account_approved
        : null

    return (
        <form noValidate onSubmit={formik.handleSubmit} {...props}>
            <SavingBar show={formik.isSubmitting} />

            {formik.errors.formError && (
                <Box sx={{ mb: 2 }}>
                    <Alert severity="error">
                        <div>{formik.errors.formError}</div>
                    </Alert>
                </Box>
            )}

            {(formik.errors.accountAccessErrors || []).length == 0 &&
                isVerified && (
                    <Box sx={{ mb: 2 }}>
                        <Alert severity="success">
                            <div>Your account is verified!</div>
                        </Alert>
                    </Box>
                )}
            {hasPasswordReset && (
                <Box sx={{ mb: 2 }}>
                    <Alert severity="success">
                        <div>
                            Your account password has been reset. You can now
                            log in, using your new password.
                        </div>
                    </Alert>
                </Box>
            )}

            {(formik.errors.accountAccessErrors || []).length == 0 &&
                isApproved != '' &&
                isApproved == '0' && (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Alert severity="info">
                                <AlertTitle>Account Approval</AlertTitle>
                                Your account is being currently being review and
                                it will be approve soon. Reviews usually take
                                less than 24 hours.
                            </Alert>
                        </Box>
                    </>
                )}

            {(formik.errors.accountAccessErrors || []).length > 0 &&
                formik.errors.accountAccessErrors.map((item, index) => (
                    <Alert
                        key={`accountAccessErrors_${index}`}
                        severity={item.type}
                        sx={{ mt: 2, mb: 2 }}
                    >
                        <AlertTitle>{item.title}</AlertTitle>
                        <div
                            dangerouslySetInnerHTML={{ __html: item.text }}
                        ></div>
                    </Alert>
                ))}

            <TextField
                error={Boolean(formik.touched.email && formik.errors.email)}
                fullWidth
                helperText={formik.touched.email && formik.errors.email}
                label="Email Address"
                margin="normal"
                name="email"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                type="email"
                value={formik.values.email}
            />
            <TextField
                error={Boolean(
                    formik.touched.password && formik.errors.password
                )}
                fullWidth
                helperText={formik.touched.password && formik.errors.password}
                label="Password"
                margin="normal"
                name="password"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                type="password"
                value={formik.values.password}
            />
            {formik.errors.submit && (
                <Box sx={{ mt: 3 }}>
                    <FormHelperText error>
                        {formik.errors.submit}
                    </FormHelperText>
                </Box>
            )}

            <AcceptanceTermsCheckbox
                value={formik.values.termsAccepted}
                onChange={(accepted, version) => {
                    formik.setFieldValue('termsAccepted', accepted)
                    formik.setFieldValue('termsVersion', version)
                }}
            />

            <Box sx={{ mt: 2 }}>
                <Button
                    disabled={
                        formik.isSubmitting || !formik.values.termsAccepted
                    }
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                >
                    Log In
                </Button>
            </Box>
        </form>
    )
}
